/**
 * Today's pieces (slice 4b spec §5, Paper "Today · routines quiet", board C):
 * each kind's tile, a Recent row, the working card, a run in Ran and a week
 * row. A chat opens in the side panel, a doc and a routine on their pages.
 */
import { Button } from "@crew/ui/components/button";
import { ThreadTile, type TileStatus } from "@crew/ui/components/crew-side-panel";
import { Link } from "@crew/ui/components/link";
import { cn } from "@crew/ui/lib/utils";
import { Check, FileText, MessageSquareReply } from "lucide-react";
import type { ReactNode } from "react";
import { ApprovalActions } from "../approvals/approval-card";
import { approvalSnippet } from "../approvals/approval-view";
import { shortTime } from "../chats/format";
import { usePanel } from "../chats/panel-state";
import { tileLetter, tileTint } from "../chats/rail";
import type { RanItem, RecentItem, Working } from "./api";
import { metaOf, ranLine, rightOf, weekDay, workingFor } from "./today-view";

const TILE = 34;
const tileBox = "flex size-[34px] shrink-0 items-center justify-center rounded-[9px]";

type ThreadLike = { id: string; title: string; icon: string | null; messaging_app: string | null };

function ChatTile({ thread, status }: { thread: ThreadLike; status: TileStatus }) {
  return (
    <ThreadTile
      letter={tileLetter(thread.title)}
      tint={tileTint(thread.id)}
      status={status}
      icon={thread.icon}
      imessage={thread.messaging_app === "imessage"}
      size={TILE}
    />
  );
}

/** A doc's tile: white, a page in copper, so it never reads as a chat. */
function DocTile() {
  return (
    <span aria-hidden data-tile="doc" className={cn(tileBox, "bg-card ring-1 ring-input")}>
      <FileText className="size-[18px] text-copper" strokeWidth={1.75} />
    </span>
  );
}

/** A chat's ring: moss while working, grey while queued, copper while it waits on them. */
const ringOf = (item: RecentItem): TileStatus => (item.kind === "chat" ? item.status : null);

function TileOf({ item }: { item: RecentItem }) {
  if (item.kind === "chat") return <ChatTile thread={item.thread} status={ringOf(item)} />;
  return <DocTile />;
}

const titleOf = (item: RecentItem) => (item.kind === "chat" ? item.thread.title : item.doc.title);

/** The row's title, as the control that opens it; its `after` covers the row. */
function RowOpener({ item, children }: { item: RecentItem; children: ReactNode }) {
  const [, setPanel] = usePanel();
  const cover =
    "text-left after:absolute after:inset-0 after:content-[''] focus-visible:outline-none focus-visible:after:rounded-lg focus-visible:after:ring-2 focus-visible:after:ring-ring";
  if (item.kind === "chat") {
    return (
      <button type="button" onClick={() => setPanel(item.thread.id)} className={cover}>
        {children}
      </button>
    );
  }
  return (
    <Link href={`/docs/${item.doc.id}`} className={cover}>
      {children}
    </Link>
  );
}

/** What a row that waits on them offers: the approval's buttons, or Reply to the crew's question. */
function RowActions({ item }: { item: RecentItem }) {
  const [, setPanel] = usePanel();
  if (item.kind !== "chat") return null;
  const open = () => setPanel(item.thread.id);
  if (item.approval) return <ApprovalActions a={item.approval} openChat={open} />;
  if (item.ask) {
    return (
      <Button size="sm" variant="outline" onClick={open} className="h-11 self-start md:h-8">
        <MessageSquareReply />
        Reply
      </Button>
    );
  }
  return null;
}

/** The row's sentence: an approval's title, an ask's question, else the item's own line. */
const lineFor = (item: RecentItem) =>
  item.kind === "chat"
    ? (item.approval?.preview.title ?? item.ask?.question ?? item.line)
    : item.line;

/** Who an approval goes to, or what it is: its snippet's head ("To aria@… · Re: …"). */
function ApprovalHead({
  approval,
}: {
  approval: NonNullable<Extract<RecentItem, { kind: "chat" }>["approval"]>;
}) {
  const head = approvalSnippet(approval.preview).head;
  return head ? <p className="truncate text-meta text-muted-foreground">{head}</p> : null;
}

/** One Recent row (spec §5): the tile, title and time, the line, the meta, and its actions. */
export function RecentRow({ item, tz, now }: { item: RecentItem; tz: string; now: Date }) {
  const right = rightOf(item, tz, now);
  const line = lineFor(item);
  return (
    <li
      data-recent={item.kind}
      data-recent-id={item.id}
      className="relative flex gap-3.5 border-t border-border py-3 first:border-t-0"
    >
      <span className="flex w-[34px] shrink-0 justify-center pt-0.5">
        <TileOf item={item} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-baseline gap-3">
          <RowOpener item={item}>
            <span className="line-clamp-1 text-body font-medium text-foreground">
              {titleOf(item)}
            </span>
          </RowOpener>
          <span
            className={cn(
              "ml-auto shrink-0 text-meta",
              right.tone === "waiting"
                ? "font-medium text-copper"
                : right.tone === "working"
                  ? "text-moss"
                  : "text-hint",
            )}
          >
            {right.text}
          </span>
        </div>
        {line ? <p className="line-clamp-2 text-sm text-copy">{line}</p> : null}
        {item.kind === "chat" && item.approval ? <ApprovalHead approval={item.approval} /> : null}
        <p className="text-xs text-muted-foreground">{metaOf(item)}</p>
        <div className="relative z-10 empty:hidden [&:not(:empty)]:pt-2">
          <RowActions item={item} />
        </div>
      </div>
    </li>
  );
}

/** A chat the crew works on now (spec §5): its tile ringed moss, the time, and the progress line. */
export function WorkingCard({ w, now }: { w: Working; now: Date }) {
  const [, setPanel] = usePanel();
  return (
    <button
      type="button"
      data-working={w.thread.id}
      onClick={() => setPanel(w.thread.id)}
      className="flex w-full items-start gap-3.5 rounded-xl bg-card p-4 text-left ring-1 ring-border transition-colors hover:bg-card/70"
    >
      <ChatTile thread={w.thread} status="working" />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex items-baseline gap-3">
          <span className="line-clamp-2 text-body font-medium text-foreground">
            {w.thread.title}
          </span>
          <span className="ml-auto shrink-0 text-meta text-moss">
            {workingFor(w.started_at, now)}
          </span>
        </span>
        {w.progress ? (
          <span className="line-clamp-2 text-meta text-copy">{w.progress.text}</span>
        ) : null}
      </span>
    </button>
  );
}

/**
 * A routine's latest run in Ran (spec §5), in the rail: a moss tick, its
 * title and when, then "Texted to you" or its lead. It opens the doc the run
 * keeps, else the routine.
 */
export function RanEntry({ item, tz, now }: { item: RanItem; tz: string; now: Date }) {
  const line = ranLine(item);
  return (
    <li
      data-ran={item.routine.id}
      className="relative flex gap-2.5 border-t border-border py-2 first:border-t-0"
    >
      <Check className="mt-0.5 size-4 shrink-0 text-moss" strokeWidth={2.2} aria-hidden />
      <div className="flex min-w-0 flex-1 flex-col gap-px">
        <div className="flex items-baseline gap-2">
          <Link
            href={item.doc_id ? `/docs/${item.doc_id}` : `/routines/${item.routine.id}`}
            className="line-clamp-2 min-w-0 flex-1 text-sm leading-5 font-medium text-foreground after:absolute after:inset-0 after:content-[''] hover:underline"
          >
            {item.routine.title}
          </Link>
          <span className="shrink-0 text-meta text-hint">{shortTime(item.at, tz, now)}</span>
        </div>
        {line ? <p className="truncate text-meta text-muted-foreground">{line}</p> : null}
      </div>
    </li>
  );
}

type WeekItem = { routine: { id: string; title: string }; at: string };

/** A routine's next run in the rail (spec §5): its day, today in copper, then time and title. */
export function WeekEntry({ item, tz, now }: { item: WeekItem; tz: string; now: Date }) {
  const { day, time } = weekDay(item.at, tz, now);
  const today = day === "Today";
  const href = `/routines/${item.routine.id}`;
  return (
    <li className="flex gap-3 border-t border-border py-2 first:border-t-0">
      <span
        className={cn(
          "w-[68px] shrink-0 text-meta leading-5 font-semibold",
          today ? "text-copper" : "text-foreground",
        )}
      >
        {day}
      </span>
      <Link
        href={href}
        className="min-w-0 flex-1 text-sm leading-5 text-copy hover:text-foreground"
      >
        {time} · {item.routine.title}
      </Link>
    </li>
  );
}
